import React from 'react';
import {
	DeprecatedContentContainer as ContentContainer,
	DeprecatedText as Text,
	deprecatedTheme as theme,
	FlexColumn,
	FlexItem,
	FlexRow,
	Icon,
} from '@forecast-it/design-system';
import {colors, spacing} from '@forecast-it/design-system/src/deprecated/shared/styles';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {NUMBER_TYPE} from '../../../../components/new-ui/project/project-budget-v3/util/BudgetUtils';
import Util from '../../../shared/util/util';
import ProgramBudgetTypeInfo from './ProgramBudgetTypeInfo';
import {PROGRAM_BUDGET_TYPE} from '../../../../constants';
import ProgramBudgetTypeProfitBox from './ProgramBudgetTypeProfitBox';
import {hasTopDownProgramBudgetFeature} from '../../../shared/util/ProgramFinancialLogic';
import ProgramUtil from '../../../shared/util/ProgramUtil';
import {ProgramHeaderSections} from './ProgramHeaderSections';

const HorizontalDivider = styled.div`
	border: solid 1px ${colors.border.primary.default};
	height: 1px;
	width: ${spacing.xl};
`;

const VerticalDivider = styled.div`
	border-left: 1px solid ${theme.colors.border.primary.default};
	height: 100%;
`;

const formatNumber = (value, numberType, currencySymbol, intl) => {
	switch (numberType) {
		case NUMBER_TYPE.MONEY:
			return Util.getFormattedNumberWithCurrency(currencySymbol, value, intl);
		case NUMBER_TYPE.PERCENTAGE:
			return Util.getFormattedPercentage(value, intl);
		default:
			break;
	}
	return value;
};

const BudgetHeaderCardWrapper = styled.div`
	margin-right: ${spacing.l};
`;

export const BudgetHeaderCard = ({children}) => {
	return (
		<BudgetHeaderCardWrapper>
			<FlexColumn gap={'xl'}>
				{children.map(child => {
					return <FlexItem>{child}</FlexItem>;
				})}
			</FlexColumn>
		</BudgetHeaderCardWrapper>
	);
};

export const FinancialNumber = ({
	translationId,
	cy,
	value,
	size = 'm',
	numberType,
	currencySymbol,
	tooltipComponent: Tooltip,
}) => {
	const intl = useIntl();
	const {formatMessage} = intl;

	return (
		<FlexColumn gap={'m'}>
			{hasTopDownProgramBudgetFeature() ? (
				<Text type={'label'} variant={'l'} color={'primary'}>
					{formatMessage({id: translationId})}
				</Text>
			) : (
				<Text type={'heading'} variant={'s'} color={'secondary'}>
					{formatMessage({id: translationId})}
				</Text>
			)}
			<FlexRow gap={'m'}>
				<Text
					type={'heading'}
					variant={size}
					color={value < 0 ? 'destructive' : size !== 'm' ? 'primary' : 'secondary'}
					data-cy={cy}
				>
					{formatNumber(value, numberType, currencySymbol, intl)}
				</Text>
				{Tooltip ? (
					<Tooltip>
						<Icon icon={'help'} size={size} />
					</Tooltip>
				) : null}
			</FlexRow>
		</FlexColumn>
	);
};

const ProgramBudgetTotals = ({program, company, actualPersonId}) => {
	const currencySymbol = Util.GetCurrencySymbol(company.currency);
	const {formatMessage} = useIntl();

	return hasTopDownProgramBudgetFeature() ? (
		<FlexColumn gap={'xl'}>
			<Text type={'heading'} variant={'l'}>
				{formatMessage({id: 'program.budget.overview'}, {Program: ProgramUtil.programText(formatMessage)})}
			</Text>

			{program.budgetType === PROGRAM_BUDGET_TYPE.UNCAPPED ? (
				<ProgramBudgetTypeInfo program={program} company={company} actualPersonId={actualPersonId} />
			) : null}

			{program.budgetType === PROGRAM_BUDGET_TYPE.FIXED_PRICE || program.budgetType === PROGRAM_BUDGET_TYPE.CAPPED ? (
				<FlexRow justifyContent={'left'} gap={'l'}>
					<FlexItem>
						<ProgramBudgetTypeInfo program={program} company={company} actualPersonId={actualPersonId} />
					</FlexItem>
					<FlexItem>
						<ProgramBudgetTypeProfitBox program={program} />
					</FlexItem>
				</FlexRow>
			) : null}

			{program.budgetType === PROGRAM_BUDGET_TYPE.UNCAPPED ? (
				<FlexRow justifyContent="left" alignItems={'flex-start'} gap={'xl'}>
					<ContentContainer width={'auto'}>
						<ProgramHeaderSections.ProfitSection program={program} />
					</ContentContainer>
					<ContentContainer width={'auto'}>
						<FlexRow justifyContent="left" alignItems={'flex-start'} gap={'xl'}>
							<ProgramHeaderSections.RevenueSection program={program} currencySymbol={currencySymbol} />
							<VerticalDivider />
							<ProgramHeaderSections.ValueOfServiceSection program={program} currencySymbol={currencySymbol} />
						</FlexRow>
					</ContentContainer>
				</FlexRow>
			) : null}

			{program.budgetType === PROGRAM_BUDGET_TYPE.FIXED_PRICE || program.budgetType === PROGRAM_BUDGET_TYPE.CAPPED ? (
				<FlexRow justifyContent="left" alignItems={'flex-start'} gap={'xl'}>
					<ContentContainer width={'auto'}>
						<FlexRow justifyContent="left" alignItems={'flex-start'} gap={'xl'}>
							{program.budgetType === PROGRAM_BUDGET_TYPE.CAPPED ? (
								<>
									<ProgramHeaderSections.ActualRevenueSection
										program={program}
										currencySymbol={currencySymbol}
									/>
									<VerticalDivider />
									<ProgramHeaderSections.ProjectedTotalRevenueSection
										program={program}
										currencySymbol={currencySymbol}
									/>
								</>
							) : null}
							{program.budgetType === PROGRAM_BUDGET_TYPE.FIXED_PRICE ? (
								<ProgramHeaderSections.RevenueSection program={program} currencySymbol={currencySymbol} />
							) : null}
							<VerticalDivider />
							<ProgramHeaderSections.ActualValueOfServiceSection
								program={program}
								currencySymbol={currencySymbol}
							/>
							<VerticalDivider />
							<ProgramHeaderSections.ProjectedTotalValueOfServiceSection
								program={program}
								currencySymbol={currencySymbol}
							/>
						</FlexRow>
					</ContentContainer>
				</FlexRow>
			) : null}
		</FlexColumn>
	) : (
		<FlexColumn gap={'xl'}>
			<Text type={'heading'} variant={'l'}>
				{formatMessage({id: 'program.budget.overview'}, {Program: ProgramUtil.programText(formatMessage)})}
			</Text>

			<FlexRow justifyContent="left" alignItems={'flex-start'} gap={'xl'}>
				<ContentContainer width={'auto'}>
					<FlexRow justifyContent="left" alignItems={'flex-start'} gap={'xl'}>
						<BudgetHeaderCard>
							<Text type={'heading'} variant={'m'} color={'primary'}>
								{formatMessage({id: 'project_budget.revenue'})}
							</Text>
							<FinancialNumber
								translationId={'common.projected_total_revenue'}
								value={program.projectsFinancialsTotals.recognitionForecastRevenue}
								numberType={NUMBER_TYPE.MONEY}
								currencySymbol={currencySymbol}
								size={'l'}
							/>
							<HorizontalDivider />
							<FinancialNumber
								translationId={'project_budget.recognised_to_date'}
								value={program.projectsFinancialsToDate.recognitionAmount}
								numberType={NUMBER_TYPE.MONEY}
								currencySymbol={currencySymbol}
							/>
						</BudgetHeaderCard>
						<BudgetHeaderCard>
							<Text type={'heading'} variant={'m'} color={'primary'}>
								{formatMessage({id: 'common.actual_value_of_service'})}
							</Text>
							<FinancialNumber
								translationId={'common.projected_total_value_of_service'}
								value={program.projectsFinancialsTotals.allTotalTimeAndExpensesAtCompletion}
								numberType={NUMBER_TYPE.MONEY}
								currencySymbol={currencySymbol}
								size={'l'}
							/>
							<HorizontalDivider />
							<FinancialNumber
								translationId={'common.actual_value_of_service'}
								value={program.projectsFinancialsTotals.allActualTimeAndExpenses}
								numberType={NUMBER_TYPE.MONEY}
								currencySymbol={currencySymbol}
							/>
						</BudgetHeaderCard>
					</FlexRow>
				</ContentContainer>
				<ContentContainer width={'auto'}>
					<BudgetHeaderCard>
						<Text type={'heading'} variant={'m'} color={'primary'}>
							{formatMessage({id: 'project_budget.profit_margin'})}
						</Text>
						<FinancialNumber
							translationId={'common.projected_total_margin'}
							value={program.projectsFinancialsTotals.recognitionProfitPercentage}
							numberType={NUMBER_TYPE.PERCENTAGE}
							size={'l'}
						/>
						<HorizontalDivider />
						<FinancialNumber
							translationId={'project_budget.actual_margin'}
							value={program.projectsFinancialsToDate.recognitionActualProfitPercentage}
							numberType={NUMBER_TYPE.PERCENTAGE}
						/>
					</BudgetHeaderCard>
				</ContentContainer>
			</FlexRow>
		</FlexColumn>
	);
};

export default ProgramBudgetTotals;
